import { defaultSnapshot, gearMods, type ProfileSnapshot } from './profile';
import { skillMods } from './skills';
import { createRng, pickWeighted, shuffle } from './rng';
import {
  ARCHETYPES,
  PERKS,
  type ArchetypeId,
  type Balance,
  type Cell,
  type CellState,
  type MapDef,
  type ModifierId,
  type PerkId,
  type RunState,
  type Unit,
} from './types';

export function createRun(
  map: MapDef,
  seed: number,
  balance: Balance,
  snapshot: ProfileSnapshot = defaultSnapshot(),
  modifiers: readonly ModifierId[] = [],
): RunState {
  const mod = (m: ModifierId): boolean => modifiers.includes(m);
  const grid: CellState[] = [];
  for (let i = 0; i < map.width * map.height; i++) grid.push({ kind: 'empty' });
  for (const c of map.closed) {
    if (c.x < 0 || c.x >= map.width || c.y < 0 || c.y >= map.height) {
      throw new Error(`map ${map.id}: closed cell out of bounds (${c.x},${c.y})`);
    }
    grid[c.y * map.width + c.x] = { kind: 'closed', cost: c.cost };
  }
  if (map.base) {
    for (let y = map.base.y; y < map.base.y + map.base.h; y++) {
      for (let x = map.base.x; x < map.base.x + map.base.w; x++) {
        if (x < 0 || x >= map.width || y < 0 || y >= map.height) throw new Error(`map ${map.id}: base out of bounds`);
        grid[y * map.width + x] = { kind: 'base' };
      }
    }
  }
  const skills = skillMods(snapshot.skills);
  let wallHp = (map.wallHp ?? balance.wallHp) + skills.wallHpAdd;
  if (mod('glass_bunker')) wallHp = Math.max(1, Math.floor(wallHp / 2));
  let startGold = (map.startGold ?? balance.startGold) + skills.startGoldAdd;
  if (mod('poor')) startGold = Math.floor(startGold / 2);
  const state: RunState = {
    mapId: map.id,
    seed,
    rng: createRng(seed),
    phase: 'placing',
    wave: 1,
    totalWaves: map.waves.length,
    endless: map.endless !== undefined,
    gold: startGold,
    wallHp,
    wallMax: wallHp,
    width: map.width,
    height: map.height,
    grid,
    buildRows: Math.min(map.height, Math.max(1, map.buildRows ?? map.height)),
    buildRowsPerWave: map.buildRowsPerWave ?? 1,
    shop: [],
    pocket: new Array<Unit | null>(balance.pocketSlots).fill(null),
    purchases: 0,
    rerollsThisWave: 0,
    wallBuys: 0,
    perks: [],
    perkOffer: [],
    nextUnitId: 1,
    kills: 0,
    merges: 0,
    lostStructures: 0,
    xp: 0,
    xpLevel: 0,
    pendingPerks: 0,
    heroLevel: mod('no_hero') ? 0 : snapshot.heroLevel,
    heroId: snapshot.heroId,
    cardLevels: { ...snapshot.cardLevels },
    heroGear: gearMods(snapshot.heroGear, balance),
    skills,
    modifiers: [...modifiers],
    battleTicks: 0,
    actions: [],
  };
  refillShop(state, balance);
  return state;
}

export function inBounds(state: RunState, c: Cell): boolean {
  return c.x >= 0 && c.x < state.width && c.y >= 0 && c.y < state.height;
}

/** Building is allowed only in the bottom `buildRows` rows: never right under the enemies' noses. */
export function canBuildAt(state: RunState, c: Cell): boolean {
  return c.y >= state.height - state.buildRows;
}

export function cellIndex(state: RunState, c: Cell): number {
  return c.y * state.width + c.x;
}

export function cellAt(state: RunState, c: Cell): CellState {
  const cs = state.grid[cellIndex(state, c)];
  if (!cs) throw new Error(`cell out of range (${c.x},${c.y})`);
  return cs;
}

export function setCell(state: RunState, c: Cell, cs: CellState): void {
  state.grid[cellIndex(state, c)] = cs;
}

export function unitAt(state: RunState, c: Cell): Unit | null {
  const cs = cellAt(state, c);
  return cs.kind === 'unit' ? cs.unit : null;
}

export function* units(state: RunState): Generator<{ cell: Cell; unit: Unit }> {
  for (let y = 0; y < state.height; y++) {
    for (let x = 0; x < state.width; x++) {
      const cs = state.grid[y * state.width + x]!;
      if (cs.kind === 'unit') yield { cell: { x, y }, unit: cs.unit };
    }
  }
}

const SHOOTER_ARCHS: readonly ArchetypeId[] = ['rapid', 'splash', 'burn', 'chain'];
const SOLDIER_ARCHS: readonly ArchetypeId[] = ['barracks', 'archery', 'stable', 'barricade'];

export function drawUnit(state: RunState, balance: Balance): ArchetypeId {
  // Challenge shops: only guns, or only camps and walls.
  const pool = state.modifiers.includes('only_shooters') ? SHOOTER_ARCHS : state.modifiers.includes('only_soldiers') ? SOLDIER_ARCHS : ARCHETYPES;
  return pickWeighted(
    state.rng,
    pool.map((a) => ({ item: a, w: balance.unitWeights[a] })),
  );
}

export function refillShop(state: RunState, balance: Balance): void {
  while (state.shop.length < balance.shopSize) state.shop.push(drawUnit(state, balance));
}

export function tableAt(table: number[], i: number): number {
  const last = table[table.length - 1];
  if (last === undefined) throw new Error('empty price table');
  return table[Math.min(i, table.length - 1)] ?? last;
}

/** Price of buying `arch` now: the purchase-count curve scaled by the archetype's base price. */
export function unitPrice(state: RunState, balance: Balance, arch: ArchetypeId): number {
  let base = Math.floor((tableAt(balance.unitPrice, state.purchases) * balance.unitBasePrice[arch] + 5) / 10);
  if (state.skills.priceDiscountPct > 0) base = Math.floor((base * (100 - state.skills.priceDiscountPct)) / 100);
  if (state.perks.includes('cheap_units')) return Math.max(1, Math.floor((base * 85) / 100));
  return Math.max(1, base);
}

/** The cheapest thing in the shop right now; the UI and the bots use it to ask "can I still buy anything?". */
export function cheapestPrice(state: RunState, balance: Balance): number {
  let best = Infinity;
  for (const a of state.shop) best = Math.min(best, unitPrice(state, balance, a));
  return best === Infinity ? 0 : best;
}

/** First empty pocket slot, or -1. */
export function freePocketSlot(state: RunState): number {
  return state.pocket.indexOf(null);
}

export function rerollPrice(state: RunState, balance: Balance): number {
  return balance.rerollBase + balance.rerollStep * state.rerollsThisWave;
}

export function wallRepairPrice(state: RunState, balance: Balance): number {
  return tableAt(balance.wallRepair.prices, state.wallBuys);
}

export function sellValue(state: RunState, unit: Unit, balance: Balance): number {
  // Refund scales with level: a level-N unit "contains" 2^(N-1) purchases.
  const price = Math.floor((tableAt(balance.unitPrice, Math.max(0, state.purchases - 1)) * balance.unitBasePrice[unit.arch] + 5) / 10);
  const contained = 1 << (unit.level - 1);
  return Math.max(1, Math.floor((price * contained * balance.sellRefundPct) / 100));
}

export function waveGold(state: RunState, wave: number, balance: Balance): number {
  let base = balance.waveGoldBase + balance.waveGoldPerWave * wave;
  base = Math.floor((base * state.skills.waveGoldPct) / 100);
  if (state.modifiers.includes('poor')) base = Math.floor((base * 70) / 100);
  if (state.perks.includes('wave_gold')) return Math.floor((base * 125) / 100);
  return base;
}

export function xpCost(state: RunState, balance: Balance): number {
  return tableAt(balance.xpCosts, state.xpLevel);
}

/** Add XP; every full bar queues one perk draft. */
export function grantXp(state: RunState, amount: number, balance: Balance): void {
  state.xp += amount;
  while (state.xp >= xpCost(state, balance)) {
    state.xp -= xpCost(state, balance);
    state.xpLevel++;
    state.pendingPerks++;
  }
}

/** If a draft is queued and perks remain, open it and switch to the perk phase. Returns whether it did. */
export function openPerkOfferIfDue(state: RunState): boolean {
  if (state.pendingPerks <= 0) return false;
  const offer = drawPerkOffer(state);
  if (offer.length === 0) {
    state.pendingPerks = 0; // Pool exhausted: XP keeps counting but nothing to draft.
    return false;
  }
  state.perkOffer = offer;
  state.phase = 'perk';
  return true;
}

export function drawPerkOffer(state: RunState): PerkId[] {
  const pool = PERKS.filter((p) => !state.perks.includes(p));
  shuffle(state.rng, pool);
  return pool.slice(0, 3);
}

/** Obstacles that can still be cleared: inside the build zone and not permanent. */
export function closedCells(state: RunState): Cell[] {
  const out: Cell[] = [];
  for (let y = 0; y < state.height; y++) {
    for (let x = 0; x < state.width; x++) {
      const c = state.grid[y * state.width + x]!;
      if (c.kind === 'closed' && c.cost > 0 && canBuildAt(state, { x, y })) out.push({ x, y });
    }
  }
  return out;
}

/** Stable, comparable snapshot for tests and server-side verification. */
export function stateDigest(state: RunState): string {
  const cells = state.grid
    .map((c) => (c.kind === 'unit' ? `${c.unit.arch}${c.unit.level}` : c.kind === 'closed' ? `#${c.cost}` : c.kind === 'base' ? 'B' : '.'))
    .join('');
  return [
    state.phase,
    state.wave,
    state.gold,
    state.wallHp,
    state.wallMax,
    state.kills,
    state.battleTicks,
    state.rng.a,
    state.shop.join(','),
    state.pocket.map((u) => (u ? `${u.arch}${u.level}` : '-')).join(','),
    state.perks.join(','),
    state.xp,
    state.xpLevel,
    state.heroLevel,
    state.heroId,
    `${state.heroGear.hpPct}/${state.heroGear.dmgPct}/${state.heroGear.speedPct}/${state.heroGear.aoePct}/${state.heroGear.aoeEveryDelta}`,
    Object.values(state.skills).join('/'),
    state.modifiers.join(','),
    ARCHETYPES.map((a) => state.cardLevels[a]).join(''),
    cells,
  ].join('|');
}
